import {payinAuditLabel} from "../../utils/payinAudit";
import React, {useEffect, useState} from "react";
import {getAuditEvents} from "../../api";
import {useAuth} from "../../auth";
import {ErrorBanner} from "../../components";
import type {AuditEventItem, FailedLoginSummary} from "../../types";
import {formatAmount, formatDate} from "../../utils";

const ACTIVITY_LIMIT = 50;

export function AdminActivityPage(): JSX.Element {
    const {token} = useAuth();

    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [traderIdInput, setTraderIdInput] = useState("");
    const [teamIdInput, setTeamIdInput] = useState("");
    const [search, setSearch] = useState("");
    const [traderId, setTraderId] = useState("");
    const [teamId, setTeamId] = useState("");
    const [showFailedLogins, setShowFailedLogins] = useState(false);
    const [failedLogins, setFailedLogins] = useState<FailedLoginSummary[]>([]);
    const [offset, setOffset] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const [items, setItems] = useState<AuditEventItem[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setTraderId(traderIdInput.trim());
            setTeamId(teamIdInput.trim());
            setOffset(0);
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput, traderIdInput, teamIdInput]);

    useEffect(() => {
        let cancelled = false;

        if (!token) {
            setItems([]);
            setHasMore(false);
            setNextOffset(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        getAuditEvents(token, {
            scope: "all",
            traderId: traderId || undefined,
            teamId: teamId || undefined,
            q: search || undefined,
            from: from || undefined,
            to: to || undefined,
            limit: ACTIVITY_LIMIT,
            offset,
            sort: "desc",
            hideFailedLogins: !showFailedLogins,
        })
            .then((resp) => {
                if (cancelled) return;
                setItems((prev) => offset === 0 ? resp.items ?? [] : [...prev, ...(resp.items ?? [])]);
                if (offset === 0) setFailedLogins(resp.failedLogins ?? []);
                setHasMore(resp.hasMore);
                setNextOffset(resp.nextOffset ?? null);
            })
            .catch((err) => {
                if (!cancelled) setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [from, offset, reloadKey, search, showFailedLogins, teamId, to, token, traderId]);

    const resetOffset = (): void => {
        setOffset(0);
    };

    const updateFrom = (value: string): void => {
        setFrom(value);
        resetOffset();
    };

    const updateTo = (value: string): void => {
        setTo(value);
        resetOffset();
    };

    const toggleFailedLogins = (value: boolean): void => {
        setShowFailedLogins(value);
        resetOffset();
    };

    const reload = (): void => {
        setOffset(0);
        setReloadKey((value) => value + 1);
    };

    const loadMore = (): void => {
        if (loading || !hasMore) return;
        setOffset(nextOffset ?? offset + ACTIVITY_LIMIT);
    };

    return (
        <div className="page admin-activity">
            <div className="page-header admin-activity-header">
                <div>
                    <h2>Платёжные действия</h2>
                    <p>Действия по платежам (завершение/отмена/спор), не полный audit journal.</p>
                </div>
                <button className="btn btn-ghost btn-sm" type="button" onClick={reload} disabled={loading}>
                    {loading ? "Обновление..." : "Обновить"}
                </button>
            </div>

            <div className="history-toolbar activity-toolbar admin-activity-toolbar">
                <div className="history-filter-groups">
                    <label className="activity-date-filter">
                        <span>From</span>
                        <input type="date" value={from} onChange={(event) => updateFrom(event.target.value)}/>
                    </label>
                    <label className="activity-date-filter">
                        <span>To</span>
                        <input type="date" value={to} onChange={(event) => updateTo(event.target.value)}/>
                    </label>
                    <label className="activity-date-filter activity-search-filter">
                        <span>Поиск</span>
                        <input
                            type="search"
                            value={searchInput}
                            placeholder="paymentId или трейдер"
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                    </label>
                    <label className="activity-date-filter">
                        <span>Trader ID</span>
                        <input
                            type="text"
                            value={traderIdInput}
                            placeholder="uuid"
                            onChange={(event) => setTraderIdInput(event.target.value)}
                        />
                    </label>
                    <label className="activity-date-filter">
                        <span>Team ID</span>
                        <input
                            type="text"
                            value={teamIdInput}
                            placeholder="uuid"
                            onChange={(event) => setTeamIdInput(event.target.value)}
                        />
                    </label>
                    <label className="activity-date-filter activity-failed-logins-toggle">
                        <span>Неудачные входы</span>
                        <span>
                            <input
                                type="checkbox"
                                checked={showFailedLogins}
                                onChange={(event) => toggleFailedLogins(event.target.checked)}
                            />
                            {" "}показывать в ленте
                        </span>
                    </label>
                </div>
            </div>

            <ErrorBanner error={error} title="Не удалось загрузить платёжные действия"/>

            {!showFailedLogins && failedLogins.length > 0 ? (
                <section className="history-list-card activity-card activity-failed-logins">
                    <div className="history-list-header">
                        <strong>Неудачные входы скрыты из ленты</strong>
                    </div>
                    <ul>
                        {failedLogins.map((row) => (
                            <li key={row.login}>
                                <strong>{row.login || "без логина"}</strong>
                                {" — "}{row.attempts} {attemptsWord(row.attempts)},{" "}
                                <span className="mono">{formatDate(row.firstAt)} — {formatDate(row.lastAt)}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            <section className="history-list-card activity-card">
                <div className="history-list-header">
                    <strong>Лента действий</strong>
                    <span className="badge">{loading && items.length === 0 ? "..." : items.length}</span>
                </div>

                {items.length === 0 ? (
                    <div className="empty-state compact">
                        {loading ? "Загрузка платёжных действий..." : "Платёжные действия не найдены"}
                    </div>
                ) : (
                    <div className="activity-table-wrap">
                        <table className="activity-table">
                            <thead>
                                <tr>
                                    <th>Когда</th>
                                    <th>Кто</th>
                                    <th>Event</th>
                                    <th>Subject</th>
                                    <th>Context</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <ActivityRow key={item.id} item={item}/>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="activity-pagination">
                    <span className="mono">Показано {items.length}</span>
                    <div>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            disabled={loading || !hasMore}
                            onClick={loadMore}
                        >
                            {loading && items.length > 0 ? "Загрузка..." : "Показать ещё"}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}

function ActivityRow({item}: {item: AuditEventItem}): JSX.Element {
    const actor = item.actorLabel || item.traderName || item.actorId || "-";
    const subject = [item.subjectType, item.subjectId].filter(Boolean).join(": ") || item.orderId || item.paymentId || "-";
    const status = item.toStatus ? `${item.fromStatus ?? "-"} -> ${item.toStatus}` : "";
    const amount = item.amountReal == null ? "" : `${formatAmount(item.amountReal)} RUB`;
    const context = [status, amount, item.currencyRate ? `rate ${item.currencyRate}` : "", payinRequisitesSummary(item)].filter(Boolean).join(" | ") || "-";

    return (
        <tr>
            <td className="mono">{formatDate(item.occurredAt)}</td>
            <td>
                <div className="activity-trader-cell">
                    <strong>{actor}</strong>
                    <span>{item.actorType}</span>
                </div>
            </td>
            <td>
                <span className="activity-type">{payinAuditLabel(item.payload?.action) ?? eventLabel(item.eventType)}</span>
            </td>
            <td className="mono admin-activity-payment-cell">{subject}</td>
            <td className="mono">{context}</td>
        </tr>
    );
}

const EVENT_LABELS: Record<string, string> = {
    order_created: "Order created",
    order_pooled: "Entered pool",
    order_claimed: "Claimed",
    receipt_uploaded: "Receipt uploaded",
    payment_completed: "Paid",
    payment_cancelled: "Cancelled",
    payment_appealed: "Appeal",
    payment_expired: "Expired",
    appeal_resolved: "Appeal resolved",
    order_partially_filled: "Partially filled",
    order_succeeded: "Order succeeded",
    auth_login_success: "Login",
    auth_login_failed: "Failed login",
    auth_logout: "Logout",
    user_created: "User created",
    merchant_updated: "Merchant updated",
    merchant_vip_changed: "VIP changed",
    merchant_overdraft_changed: "Overdraft settings changed",
    merchant_rate_source_changed: "Rate source changed",
    merchant_archived: "Merchant archived",
    merchant_balance_reset: "Merchant balance reset",
    merchant_balance_adjusted: "Пополнение / вывод",
    merchant_deposit_address_set: "TRC20-адрес изменён",
    merchant_chain_deposit_credited: "Автопополнение TRC20",
    merchant_chain_backfill_completed: "TRC20 backfill завершён",
    merchant_token_issued: "Token issued",
    merchant_token_revoked: "Token revoked",
    team_created: "Team created",
    team_balance_reset: "Team balance reset",
    payin_requisites_set: "Реквизиты приёма введены",
};

function attemptsWord(count: number): string {
    const tens = count % 100;
    const ones = count % 10;
    if (tens >= 11 && tens <= 14) return "попыток";
    if (ones === 1) return "попытка";
    if (ones >= 2 && ones <= 4) return "попытки";
    return "попыток";
}

function eventLabel(type: string): string {
    return EVENT_LABELS[type] ?? type;
}

function payinRequisitesSummary(event: AuditEventItem): string {
    if (event.eventType !== "payin_requisites_set" || !event.payload) return "";
    const bankName = typeof event.payload.bankName === "string" ? event.payload.bankName : "";
    const cardMask = typeof event.payload.cardMask === "string" ? event.payload.cardMask : "";
    const holder = event.payload.cardHolderPresent ? "ФИО указано" : "";
    return [bankName, cardMask, holder].filter(Boolean).join(" ");
}
